import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Search, X, Share2 } from 'lucide-react';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import TableSkeleton from 'shared/components/ui/TableSkeleton';
import {
  activitySearchWrapperClass, activitySearchInputClass, tableHeaderRowClass,
  tableHeaderCellClass, tableRowClass, tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';
import { listDashboards, createDashboard } from 'features/dashboards/api/dashboardsApi';
import { timeAgo, errorText } from 'features/dashboards/format';
import Avatar from 'shared/components/ui/Avatar';
import DashboardDetailsDialog from 'features/dashboards/components/DashboardDetailsDialog';

/** People as overlapping pictures; the rest counted. */
function People({ people, max = 4 }) {
  if (!people.length) return <span className="text-[color:var(--text-muted)]">—</span>;
  const shown = people.slice(0, max);
  return (
    <span className="inline-flex items-center" title={people.map((u) => `${u.name || u.email}${u.owner ? ' (owner)' : ''}`).join(', ')}>
      {shown.map((u, i) => (
        <span key={u.id} className={`rounded-full ring-2 ring-[color:var(--surface-base)] ${i ? '-ml-1.5' : ''}`}>
          <Avatar name={u.name || u.email} src={u.avatar_url} size={22} />
        </span>
      ))}
      {people.length > max ? <span className="ml-1.5 text-[11px] text-[color:var(--text-muted)]">+{people.length - max}</span> : null}
    </span>
  );
}

const COLUMNS = ['Name', 'Description', 'Members', 'Updated'];

export default function DashboardsListPage() {
  useBreadcrumb([{ label: 'Reports' }]);
  const { team } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems((await listDashboards(team)).filter((d) => !d.is_default)); } catch (e) { setError(errorText(e)); } finally { setLoading(false); }
  }, [team]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((d) => (d.name || '').toLowerCase().includes(q)) : items;
  }, [items, query]);

  const open = (id) => navigate(`/${encodeURIComponent(team)}/reports/${id}`);

  const create = async ({ name, description }) => {
    const { id } = await createDashboard(team, { name, description });
    open(id);
  };

  return (
    <main className="relative flex flex-1 min-h-0 flex-col overflow-y-auto bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="sticky top-0 z-10 flex flex-col gap-2 bg-surface px-3 sm:px-4 pb-2 pt-3">
        <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px]">
          <span className="font-semibold tabular-nums text-[color:var(--text-primary)]">{loading ? '…' : filtered.length.toLocaleString()}</span>
          <span className="text-[color:var(--text-muted)]">{filtered.length === 1 ? 'report' : 'reports'}</span>
        </div>
        <div className={`${activitySearchWrapperClass} ml-auto w-[220px]`}>
          <Search size={13} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search reports…" className={activitySearchInputClass} />
          {query ? <button type="button" onClick={() => setQuery('')} className="flex-none text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]" aria-label="Clear"><X className="h-3.5 w-3.5" /></button> : null}
        </div>
        <button type="button" onClick={() => setCreating(true)}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[color:var(--trodo-primary)] px-3 text-[12px] font-medium text-[color:var(--text-on-brand)] hover:opacity-90 disabled:opacity-50 whitespace-nowrap">
          <Plus className="w-3.5 h-3.5" /> New report
        </button>
        </div>
      </div>

      {error && <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-[color:var(--feedback-error-bg)] border border-[color:var(--feedback-error)] text-[12.5px] text-[color:var(--feedback-error)]">{error}</div>}

      <div className="flex-1 min-h-0 px-4 pb-8 pt-3">
        {loading ? (
          <TableSkeleton columns={COLUMNS} rows={6} />
        ) : (
          <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
            <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className={tableHeaderRowClass}>
                  {COLUMNS.map((label) => <th key={label} className={tableHeaderCellClass}>{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-[12px] text-[color:var(--text-muted)]">{query.trim() ? `No reports match "${query.trim()}"` : 'No reports yet'}</td></tr>
                )}
                {filtered.map((d) => (
                  <tr key={d.id} className={tableRowClass} onClick={() => open(d.id)}>
                    <td className={`${tableDataCellClass} max-w-0 w-[32%]`}>
                      <div className="flex min-w-0 items-center">
                        <span className="truncate font-medium">{d.name}</span>
                        {d.shared ? <Share2 size={13} className="ml-2 shrink-0 text-[color:var(--text-muted)]" role="img" aria-label="Shared with a public link" title="Shared with a public link" /> : null}
                      </div>
                    </td>
                    <td className={`${tableDataCellClass} max-w-0 w-[40%] text-[color:var(--text-muted)]`}>
                      <div className="truncate text-[color:var(--text-muted)]" title={d.description || undefined}>{d.description || '—'}</div>
                    </td>
                    <td className={tableDataCellClass}><People people={[...(d.owner ? [{ ...d.owner, owner: true }] : []), ...(d.editors || [])]} /></td>
                    <td className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>{timeAgo(d.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
      {creating ? (
        <DashboardDetailsDialog title="New report" confirmLabel="Create" onSubmit={create} onClose={() => setCreating(false)} />
      ) : null}
    </main>
  );
}
